/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, EventEmitter, Output } from '@angular/core';
import {
  ContactDetailsDialogConfig,
  contactDetailsDialogRoute,
  FormGroupSchemaConfig,
} from '@gh/builder-shared';
import { ContactType } from '@gh/builder-shared-data';
import {
  AbstractSnapshotFormComponent,
  DateTimeTypes,
  FormState,
  FormStateBuilder,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  updateSchemaWithChildren,
  UserContext,
  whenAllOf,
  whenCreateOrEditMode,
  whenEditMode,
} from '@gh/common-shared';
import {
  AutocompleteLoadMoreEvent,
  DialogService,
  getControl,
  getControlValue,
  markControlAsDirty,
  setControlValue,
  validationError,
} from '@gh/core-ui';
import { currentNormalizedDate, isNotNil, nullate } from '@gh/core-util';
import { filter$ } from '@gh/rx/operators';
import { isNil } from 'lodash';
import { JobCsDetailsBusinessService } from '../../../resources/job-cs-details-business.service';
import { JobCsConsultant, JobCsDetailsMasterInfo } from '../../../resources/job-cs-details.model';

@Component({
  standalone: false,
  selector: 'gh-job-cs-details-top-form',
  templateUrl: 'job-cs-details-top-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobCsDetailsTopFormComponent extends AbstractSnapshotFormComponent<JobCsDetailsMasterInfo> {
  @Output() openDocument = new EventEmitter<void>();
  @Output() preview = new EventEmitter<void>();
  @Output() contactMenuChange = new EventEmitter<void>();

  constructor(private formStateBuilder: FormStateBuilder,
              private userContext: UserContext,
              private dialogs: DialogService,
              private business: JobCsDetailsBusinessService) {
    super();
  }

  loadMoreEmployees(event: AutocompleteLoadMoreEvent): void {
    event.load(this.business.findEmployeesByName(event.query));
  }

  onOpenEmployee(): void {
    this.openContact(getControlValue(this.form.group, 'colorConsultant'));
  }

  onCreateEmployee(): void {
    this.openContact();
  }

  onSignByClient(signed: boolean): void {
    if (signed) {
      setControlValue(this.form.group, 'csSignedByClientDate', currentNormalizedDate());
      markControlAsDirty(this.form.group, 'csSignedByClientDate');
    } else {
      // reset client and office date
      setControlValue(this.form.group, 'csSignedByClientDate', void 0);
      markControlAsDirty(this.form.group, 'csSignedByClientDate');
      setControlValue(this.form.group, 'csSignedByOffice', false);
      setControlValue(this.form.group, 'csSignedByOfficeDate', void 0);
      markControlAsDirty(this.form.group, 'csSignedByOfficeDate');
      // reset document id
      setControlValue(this.form.group, 'csDocSigningId', void 0);
      markControlAsDirty(this.form.group, 'csDocSigningId');
    }
    // document is signed manually for any change
    setControlValue(this.form.group, 'isSignedManually', true);
  }

  onSignByOffice(signed: boolean): void {
    if (signed) {
      setControlValue(this.form.group, 'csSignedByOfficeDate', currentNormalizedDate());
      markControlAsDirty(this.form.group, 'csSignedByOfficeDate');
    } else {
      setControlValue(this.form.group, 'csSignedByOfficeDate', void 0);
      markControlAsDirty(this.form.group, 'csSignedByOfficeDate');
      // reset document id
      setControlValue(this.form.group, 'csDocSigningId', void 0);
      markControlAsDirty(this.form.group, 'csDocSigningId');
    }
    // document is signed manually for any change
    setControlValue(this.form.group, 'isSignedManually', true);
  }

  onDateGroupChanged(): void {
    // validate date fields
    getControl(this.form.group, 'csSignedByClientDate').updateValueAndValidity();
    getControl(this.form.group, 'csSignedByOfficeDate').updateValueAndValidity();
    // reset document id
    setControlValue(this.form.group, 'csDocSigningId', void 0);
    markControlAsDirty(this.form.group, 'csDocSigningId');
    // document is signed manually for any change
    setControlValue(this.form.group, 'isSignedManually', true);
  }

  onOpenDocument(): void {
    this.openDocument.emit();
  }

  onPreview(): void {
    this.preview.emit();
  }

  openCustomer(): void {
    this.dialogs.openWithInput(contactDetailsDialogRoute, <ContactDetailsDialogConfig>{
      contactType: ContactType.Customer,
      contactId: this.state.value.contactId,
      parentTitle: 'lbl_job_color_selection',
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((contact: any) => {
        this.contactMenuChange.emit();
        setControlValue(this.form.group, 'customerName', contact.name);
        this.state.value.contactId = contact.contactId;
      });
  }

  protected buildForm(snapshot: FormStateSnapshot<JobCsDetailsMasterInfo>): FormState<JobCsDetailsMasterInfo> {
    return this.formStateBuilder.buildPlain({
      snapshot,
      mode: this.mode,
      enabled: whenCreateOrEditMode(),
      ...this.getFormSchemaConfig(getFormStateSnapshotValue(snapshot)),
    });
  }

  protected afterRestored(current: FormStateSnapshot<JobCsDetailsMasterInfo>): void {
    updateSchemaWithChildren(this.form.group, this.getFormSchemaConfig(getFormStateSnapshotValue(current)));
  }

  // TODO add for customer type
  private openContact(openedContact?: JobCsConsultant): void {
    this.dialogs.openWithInput(contactDetailsDialogRoute, <ContactDetailsDialogConfig>{
      contactType: ContactType.Employee,
      contactId: openedContact ? openedContact.contactId : void 0,
      parentTitle: 'lbl_job_color_selection',
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((contact: JobCsConsultant) => {
        // Emit event to indicate contact menu change before updating form
        this.contactMenuChange.emit();
        setControlValue(this.form.group, 'colorConsultant', contact);
      });
  }

  private getFormSchemaConfig(masterInfo: JobCsDetailsMasterInfo): FormGroupSchemaConfig {
    return {
      children: {
        jobNumber: { enabled: false },
        customerName: { enabled: false },
        siteAddress: { enabled: false },
        colorConsultant: { required: true },
        csSignedByClient: { enabled: whenAllOf(whenEditMode(), !!masterInfo.produced) },
        csSignedByClientDate: {
          required: masterInfo.csSignedByClient,
          enabled: masterInfo.csSignedByClient && !!masterInfo.produced,
          type: DateTimeTypes.ShortDate,
          validators: [() => {
            if (isNil(this.form)) {
              return nullate(void 0);
            }
            const csSignedByClientDate = getControlValue(this.form.group, 'csSignedByClientDate');
            const csSignedByOfficeDate = getControlValue(this.form.group, 'csSignedByOfficeDate');
            return isNil(csSignedByClientDate) || isNil(csSignedByOfficeDate)
            || csSignedByClientDate <= csSignedByOfficeDate
              ? nullate(void 0)
              : { dateRange: validationError('msg_field_invalid_format') };
          }],
        },
        csSignedByOffice: {
          enabled: whenAllOf(whenEditMode(), masterInfo.csSignedByClient, isNotNil(masterInfo.csSignedByClientDate), !!masterInfo.produced),
        },
        csSignedByOfficeDate: {
          required: masterInfo.csSignedByClient && masterInfo.csSignedByOffice,
          enabled: masterInfo.csSignedByClient && masterInfo.csSignedByOffice && !!masterInfo.produced,
          type: DateTimeTypes.ShortDate,
          validators: [() => {
            if (isNil(this.form)) {
              return nullate(void 0);
            }
            const csSignedByClientDate = getControlValue(this.form.group, 'csSignedByClientDate');
            const csSignedByOfficeDate = getControlValue(this.form.group, 'csSignedByOfficeDate');
            return isNil(csSignedByClientDate) || isNil(csSignedByOfficeDate)
            || csSignedByClientDate <= csSignedByOfficeDate
              ? nullate(void 0)
              : { dateRange: validationError('msg_field_invalid_format') };
          }],
        },
        csDocSigningId: void 0,
        isSignedManually: void 0,
        produced: void 0,
        isClientSignedOnOrAfterOffice: { enabled: false },
      },
    };
  }
}
